<template>
  <div class="blog-main-content">
    <BlogList class="right"/>
    <h1>Nova + Energy Shield Sorceress Build Guide</h1>
    <p>The Nova Energy Shield Sorceress is a powerful build that relies on <strong>Nova</strong> for high AoE damage and <strong>Energy Shield</strong> for survivability. With strong mana regeneration and high burst potential, this build excels at clearing large areas efficiently.</p>

    <div>
      <img class="build-screenshot" alt="build screenshot" src="@/assets/builds/bd-nova-sor.png">
    </div>
    
    <div class="section">
        <h2>Recommended Gear</h2>
        <Equipments
          class="right"
          :left-weapon="weapon"
          :helm="helm"
          :armor="armor"
          :gloves="gloves"
          :belt="belt"
        />
        <h3>Weapon</h3>
        <ul>
            <li>Infinity (Scythe) – Core weapon to reduce enemy lightning resistance</li>
        </ul>
        <h3>Helmet</h3>
        <ul>
            <li>Griffon's Eye – Best for lightning damage boost</li>
        </ul>
        <h3>Armor</h3>
        <ul>
            <li>Skin of the Vipermagi – Faster Cast Rate (FCR), Increase all Resistances and +1 Skills</li>
        </ul>
        <h3>Gloves</h3>
        <ul>
            <li>Magefist – Faster Cast Rate (FCR) and mana regeneration</li>
            <li>Trang-Oul's Claws – Faster Cast Rate, Cold Resistance</li>
        </ul>
        <h3>Belt</h3>
        <ul>
            <li>Arachnid Mesh – Faster Cast Rate (FCR) and +1 Skills</li>
        </ul>
        <h3>Rings</h3>
        <ul>
            <li>Stone of Jordan – Increases skills and mana</li>
            <li>If FCR is below 105%, use a rare or crafted ring with 10% FCR</li>
        </ul>
        <h3>Amulet</h3>
        <ul>
            <li>Mara’s Kaleidoscope – +2 Skills, All Resistances</li>
            <li>A +3 Lightning Skills amulet or +2 Sorceress Skills crafted amulet</li>
        </ul>
        <h3>Inventory</h3>
        <ul>
            <li><strong>Crack of the Heavens</strong> – Used to break lightning immunities of monsters.</li>
        </ul>
    </div>

    <div id="slot-content_mpu" class="content-ad-mpu"/>

    <div class="section">
        <h2>Skill Allocation</h2>
        <div class="m-3 skills-tab right">
          <b-tabs small content-class="m-2" class="tab-text">
            <b-tab title="Cold">
              <img class="w-100" src="@/assets/builds/skills-nova-sor-1.png">
            </b-tab>
            <b-tab title="Lightning" active>
              <img class="w-100" src="@/assets/builds/skills-nova-sor-2.png">
            </b-tab>
            <b-tab title="Fire">
              <img class="w-100" src="@/assets/builds/skills-nova-sor-3.png">
            </b-tab>
          </b-tabs>
        </div>
        <h3>Core Skills (Max 20 Points Each)</h3>
        <ul>
            <li>Nova – Primary damage skill</li>
            <li>Lightning Mastery – Boosts all lightning damage</li>
            <li>Static Field – Reduces enemy HP (1 point is enough)</li>
        </ul>
        <h3>Survivability Skills</h3>
        <ul>
            <li>Energy Shield – Primary defensive skill</li>
            <li>Telekinesis – Improves Energy Shield efficiency, should be maxed</li>
            <li>Teleport – Essential for mobility (1 point)</li>
        </ul>
        <h3>Support Skills</h3>
        <ul>
            <li>Frozen Armor – Increases defense (1 point)</li>
            <li>Frost Nova – Slow down Enemies (1 point)</li>
        </ul>
    </div>
    
    <div class="section">
        <h2>Stat Distribution</h2>
        <ul>
            <li><strong>Strength:</strong> Just enough to equip gear</li>
            <li><strong>Dexterity:</strong> No investment</li>
            <li><strong>Vitality:</strong> Balanced with Energy</li>
            <li><strong>Energy:</strong> Balanced with Vitality</li>
            <li>Make sure HP is slightly higher than MP to avoid Blood Mana curse from Succubus or Baal</li>
        </ul>
    </div>

    <div class="section">
        <h2>Mercenary Choice</h2>
        <p>Recommended: <strong>Act 2 Prayer Aura Mercenary</strong>, equipped with:</p>
        <ul>
            <li><strong>Insight</strong> (Runeword) – Provides Meditation Aura for mana regeneration</li>
            <li><strong>Cure</strong> (Runeword) – Additional healing for survivability</li>
            <li><strong>Chains of Honor</strong> (Runeword) – Boosts resistances and survivability</li>
        </ul>
    </div>
    
    <div class="section">
        <h2>Gameplay Tips</h2>
        <h4>1. Combat Rotation</h4>
        <ul>
            <li>Use <strong>Static Field</strong> to weaken enemies before spamming <strong>Nova</strong></li>
        </ul>
        <h4>2. Survivability</h4>
        <ul>
            <li>Energy Shield absorbs damage; maintain high mana with <strong>Meditation Aura</strong> from the mercenary</li>
        </ul>
        <h4>3. Mana Management</h4>
        <ul>
            <li>Since this build relies on <strong>Energy Shield</strong>, maintaining high mana is critical</li>
        </ul>
    </div>

  </div>
</template>

<script>
import BlogList from '../BlogList.vue'
import Equipments from './Equipments.vue'

export default {
  name: 'NovaSor',

  components: {
    BlogList,
    Equipments,
  },

  computed: {
    weapon () {
      return {
        'img': 'eq-scythe',
        'width': 80,
        'title': 'Infinity Runeword',
        'runes': 'Ber + Mal + Ber + Ist',
        'detail': [
          '50% Chance To Cast Level 20 Chain Lightning When You Kill An Enemy',
          'Level 12 Conviction Aura When Equipped',
          '+35% Faster Run/Walk',
          '+255-325% Enhanced Damage',
          '-(45-55)% To Enemy Lightning Resistance',
          '40% Chance of Crushing Blow',
          'Prevent Monster Heal',
          '0.5-49.5 To Vitality (Based on Character Level)',
          '30% Better Chance of Getting Magic Items',
          'Level 21 Cyclone Armor (30 Charges)',
        ],
      }
    },

    helm () {
      return {
        'img': 'eq-diadem',
        'width': 75,
        'title': "Griffon's Eye",
        'detail': [
          'Defense: 150-260', 
          '+100-200 Defense',
          '+1 To All Skills',
          '+25% Faster Cast Rate', 
          '-15-20% To Enemy Lightning Resistance',
          '+10-15% To Lightning Skill Damage'
        ],
      }
    },

    armor () {
      return {
        'img': 'eq-skin-of-viper',
        'width': 75,
        'title': 'Skin of the Vipermagi',
        'detail': [
          'Defense: 279',
          '+120% Enhanced Defense',
          '+1 To All Skills', 
          '30% Faster Cast Rate',
          'Magic Damage Reduced By 9-13',
          'All Resistances +20-35', 
        ],
      }
    },

    gloves () {
      return {
        'img': 'eq-magefist',
        'width': 70,
        'title': 'Magefist',
        'detail': [
          'Defense: 24-25', 
          '+20-30% Enhanced Defense',
          '+10 Defense',
          '+1 To Fire Skills', 
          '+20% Faster Cast Rate',
          '+30% Faster Hit Recovery',
          'Regenerate Mana 25%',
          'Adds 1-6 Fire Damage',
        ],
      }
    },

    belt () {
      return {
        'img': 'eq-arachnid-mesh',
        'width': 75,
        'title': 'Arachnid Mesh',
        'detail': [
          'Defense: 119-138', 
          '+90-120% Enhanced Defense',
          'Slows Target By 10%',
          '+1 To All Skills', 
          '+20% Faster Cast Rate',
          'Increases Maximum Mana 5%',
          'Level 3 Venom (11 Charges)',
        ],
      }
    },
  }
}
</script>
